import React, { useEffect } from 'react'
import Helmet from 'react-helmet'
import queryString from 'query-string'
import { Location } from 'history'

import {
  useQueryParams,
  RouteProps,
  routes,
  IndexLayoutOldTitle,
  RedirectBadge,
  IndexLayoutExtraProps,
  IndexLayoutContent,
  SignInForm,
  useAnalytics,
  IndexLayoutHeaderLink,
  getIsForInAppPresentation,
} from '@sketch/modules-common'

import { SketchLoginDeprecationModal } from '@sketch/user'
import {
  DocumentHead,
  Link,
  OrderedBreadcrumbs,
  useModalContext,
} from '@sketch/components'

import {
  BreadcrumbsWrapper,
  StyledLinkButton,
  FormContainer,
  BottomSection,
  Paragraph,
  Line,
} from './SignInView.styles'

const SIGN_IN_CRUMBS = [{ content: 'Sign In' }, { content: 'Create Workspace' }]

interface LocationState {
  from?: Location
  // In some cases, we need to force the users to login with email and password
  // for their SSO account, for example, when accepting a workspace invitation
  hideSSO?: boolean
}

interface SignInViewProps extends RouteProps<'SIGN_IN'>, IndexLayoutExtraProps {
  location: Location<LocationState>
}

const SignInView: React.FC<SignInViewProps> = ({
  history,
  location,
  HeaderPortal,
}) => {
  const { sketch } = useQueryParams<'SIGN_IN'>()
  const { trackSimpleAnalytics, removeSimpleAnalytics } = useAnalytics()

  const { state } = location
  const linkState = { from: state?.from }
  const showSSOSignInButton = !state?.hideSSO

  useEffect(() => {
    trackSimpleAnalytics()
    return () => removeSimpleAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { email }: { email?: string } = queryString.parse(
    state?.from?.search || ''
  )

  const { showModal } = useModalContext()
  const isForInAppPresentation = getIsForInAppPresentation()

  const willContinueToWorkspaceCreation =
    state?.from?.pathname === routes.WORKSPACE_CREATE.create({})

  return (
    <IndexLayoutContent center="horizontal" marginTop paddingHorizontal>
      <HeaderPortal>
        <IndexLayoutHeaderLink />
      </HeaderPortal>
      <Helmet>
        <link rel="canonical" href="https://www.sketch.com/signin/" />
        <meta property="og:url" content="https://www.sketch.com/signin/" />
      </Helmet>
      <DocumentHead title="Sign in - It's great to see you again" />
      <>
        {state?.from && <RedirectBadge from={state.from} {...state} />}

        {willContinueToWorkspaceCreation && (
          <BreadcrumbsWrapper>
            <OrderedBreadcrumbs crumbs={SIGN_IN_CRUMBS} currentCrumb={0} />
          </BreadcrumbsWrapper>
        )}

        <IndexLayoutOldTitle>Sign in to Sketch</IndexLayoutOldTitle>
        <FormContainer>
          <SignInForm
            email={email}
            onCompleted={data => {
              // MFA auth
              if (data.signIn.__typename === 'MFACredentials') {
                history.push(routes.VERIFICATION_CODE.create({}), {
                  mfaToken: data.signIn.mfaToken,
                  from: state?.from,
                })
                return
              }

              // U+P auth
              if (sketch === 'true') {
                showModal(SketchLoginDeprecationModal)
              }

              // Partner invitation URL contains the "partner" parameter. This
              // allow us to go directly to the subscribe page without passing
              // on AcceptWorkspaceInviteView and be redirected from there.
              const destinationRouteParams = new URLSearchParams(
                state?.from?.search
              )
              const isPartner = destinationRouteParams.get('partner')
              const token = destinationRouteParams.get('token')
              if (isPartner && token) {
                history.push(
                  routes.WORKSPACE_SUBSCRIBE_PARTNER.create({
                    query: {
                      token,
                    },
                  })
                )
                return
              }

              if (willContinueToWorkspaceCreation) {
                history.push(routes.WORKSPACE_CREATE.create({}), {
                  fromSignIn: true,
                })
              } else {
                history.push(state?.from || routes.ENTRY.create({}))
              }
            }}
          />

          {showSSOSignInButton && (
            <StyledLinkButton
              size="40"
              variant="secondary"
              to={{
                pathname: routes.SSO_SIGN_IN.create({}),
                state: linkState,
              }}
            >
              Sign in with SSO
            </StyledLinkButton>
          )}
        </FormContainer>

        <Line />
        <BottomSection>
          {!isForInAppPresentation && (
            <Paragraph>
              New to Sketch?{' '}
              <Link
                variant="primary"
                isUnderlined
                to={{
                  pathname: routes.SIGN_UP.create({}),
                  state,
                }}
              >
                Create an account
              </Link>
            </Paragraph>
          )}
        </BottomSection>
      </>
    </IndexLayoutContent>
  )
}

export default SignInView
